import { Outlet, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, BookOpen, Users, ShoppingCart, MessageSquare, FileText, Tag, LogOut, Menu, X, Home, Flag, Mail } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/stores/auth';

function Ic({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}

const S = {
  font: "'Sora', system-ui, sans-serif",
  heading: "'Fraunces', Georgia, serif",
};

export default function AdminLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { href: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { href: '/admin/courses', icon: BookOpen, label: 'Courses' },
    { href: '/admin/users', icon: Users, label: 'Users' },
    { href: '/admin/orders', icon: ShoppingCart, label: 'Orders' },
    { href: '/admin/messages', icon: MessageSquare, label: 'Messages' },
    { href: '/admin/exam-requests', icon: FileText, label: 'Exam Requests' },
    { href: '/admin/exam-products', icon: ShoppingCart, label: 'Exam Products' },
    { href: '/admin/exam-orders', icon: ShoppingCart, label: 'Exam Orders' },
    { href: '/admin/coupons', icon: Tag, label: 'Coupons' },
    { href: '/admin/feature-flags', icon: Flag, label: 'Feature Flags' },
    { href: '/admin/bulk-email', icon: Mail, label: 'Bulk Email' },
    { href: '/admin/invoices', icon: FileText, label: 'Invoices' },
  ];

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  const isActive = (href: string) => location.pathname === href;

  return (
    <div className="soleil" style={{ minHeight: '100vh', fontFamily: S.font }}>
      {/* Header */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 50, height: 64,
        background: 'linear-gradient(135deg, var(--soleil-dark), var(--soleil-d2))',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%', padding: '0 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="md:hidden"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--soleil-cream)', padding: 4 }}
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <Link to="/admin" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Ic size={15} />
              </div>
              <span style={{ fontFamily: S.heading, fontSize: 16, fontWeight: 600, color: 'var(--soleil-cream)' }}>Admin Panel</span>
            </Link>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Link to="/" style={{ fontFamily: S.font, fontSize: 13, color: 'rgba(250,248,244,0.65)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
              <Home size={14} /> View Site
            </Link>
            <span className="hidden md:block" style={{ fontFamily: S.font, fontSize: 13, color: 'rgba(250,248,244,0.65)' }}>{user?.name}</span>
            <button onClick={handleLogout} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(250,248,244,0.65)', padding: 6 }}>
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <div style={{ display: 'flex' }}>
        {/* Sidebar */}
        <aside
          className={`${sidebarOpen ? 'block' : 'hidden'} md:block`}
          style={{
            width: 256, minHeight: 'calc(100vh - 64px)',
            background: 'var(--soleil-cream)', borderRight: '1px solid var(--soleil-sand)',
            padding: 12, position: sidebarOpen ? 'fixed' : undefined, zIndex: 40,
          }}
        >
          <nav style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 11,
                  padding: '9px 14px', borderRadius: 10, textDecoration: 'none',
                  fontFamily: S.font, fontSize: 13, fontWeight: isActive(item.href) ? 600 : 500,
                  background: isActive(item.href) ? 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))' : 'transparent',
                  color: isActive(item.href) ? 'var(--soleil-dark)' : 'var(--soleil-sub)',
                  transition: 'all 0.15s ease',
                }}
              >
                <item.icon size={17} />
                {item.label}
              </Link>
            ))}
          </nav>
        </aside>

        {/* Main */}
        <main style={{ flex: 1, padding: 24, background: 'var(--soleil-cream)', minHeight: 'calc(100vh - 64px)' }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
